import { Box, Paper, Stack, Typography, alpha, useTheme } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  DataGrid,
  GridOverlay,
} from '@mui/x-data-grid';
import type { Student } from '@/types/school';
import { STUDENT_COLUMNS } from './StudentTableColumns';
import TableToolbar from './TableToolbar';

interface StudentTableProps {
  rows: Student[];
  isLoading: boolean;
  error: string;
  onGenerateMagicLink: () => void;
  isGenerateMagicLinkDisabled?: boolean;
}

function ErrorOverlay({ message }: { message: string }) {
  return (
    <GridOverlay>
      <Stack alignItems="center" gap={0.5} sx={{ color: 'error.main' }}>
        <ErrorOutlineIcon fontSize="small" />
        <Typography variant="body2" color="error">
          {message}
        </Typography>
      </Stack>
    </GridOverlay>
  );
}

export default function StudentTable({
  rows,
  isLoading,
  error,
  onGenerateMagicLink,
  isGenerateMagicLinkDisabled = false,
}: StudentTableProps) {
  const theme = useTheme();
  const borderColor = alpha(theme.palette.divider, 1);

  const hasError = Boolean(error);
  const displayRows = hasError ? [] : rows;

  return (
    <Box>
      <TableToolbar
        onGenerateMagicLink={onGenerateMagicLink}
        isGenerateMagicLinkDisabled={isGenerateMagicLinkDisabled}
      />

      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${borderColor}`,
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <DataGrid
          rows={displayRows}
          columns={STUDENT_COLUMNS}
          getRowId={(row) => row.userId}
          loading={isLoading}
          disableColumnMenu
          disableColumnResize
          hideFooter
          autoHeight
          sortingOrder={['asc', 'desc']}
          initialState={{
            sorting: { sortModel: [{ field: 'fullName', sort: 'asc' }] },
          }}
          slots={{
            ...(hasError ? { noRowsOverlay: () => <ErrorOverlay message={error} /> } : {}),
          }}
          localeText={{
            noRowsLabel: 'No students found for the selected filters.'
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              borderBottom: `1px solid ${borderColor}`,
            },
            '& .MuiDataGrid-row': {
              '&:last-child .MuiDataGrid-cell': { borderBottom: 'none' },
            },
            '& .MuiDataGrid-overlayWrapper': {
              minHeight: 120,
            },
          }}
        />

        {/* Footer row */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            py: 1.25,
            borderTop: `1px solid ${borderColor}`,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Showing {displayRows.length} {displayRows.length === 1 ? 'student' : 'students'}
          </Typography>

        </Stack>
      </Paper>
    </Box>
  );
}
